/**
 * Hardcoded role permission templates mirrored from backend base.yaml.
 *
 * Source of truth: pylibs/surface_core/.../permissions/config/base.yaml
 * Update manually when roles change until a staff API exposes permissions.
 */

import type { ComboboxOption } from '@surface/components';

import { Action, Permission, Resource, SystemRoleType } from '@/api/client';

export type RolePermissionTemplate = {
  label: string;
  systemType: SystemRoleType;
  permissions: Permission[];
};

const resourceValues = new Set<string>(Object.values(Resource));
const actionValues = new Set<string>(Object.values(Action));

/** Parses `Resource.Action` strings from role YAML into API Permission objects. */
export function parsePermissionString(raw: string): Permission | null {
  const dotIdx = raw.indexOf('.');
  if (dotIdx === -1) return null;

  const resource = raw.slice(0, dotIdx);
  const action = raw.slice(dotIdx + 1);

  if (!resourceValues.has(resource) || !actionValues.has(action)) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[role-permission-templates] Skipping unknown permission: ${raw}`
      );
    }
    return null;
  }

  return {
    resource: resource as Resource,
    action: action as Action,
  };
}

function permissionsFromStrings(rawPermissions: string[]): Permission[] {
  const seen = new Set<string>();
  const permissions: Permission[] = [];

  for (const raw of rawPermissions) {
    const parsed = parsePermissionString(raw);
    if (!parsed) continue;
    const key = `${parsed.resource}:${parsed.action}`;
    if (seen.has(key)) continue;
    seen.add(key);
    permissions.push(parsed);
  }

  return permissions;
}

function defineRole(
  name: string,
  label: string,
  systemType: SystemRoleType,
  permissionStrings: string[]
): [string, RolePermissionTemplate] {
  return [
    name,
    {
      label,
      systemType,
      permissions: permissionsFromStrings(permissionStrings),
    },
  ];
}

/** Role name → template (matches backend `roles[].name`). */
export const ROLE_PERMISSION_TEMPLATES: Record<string, RolePermissionTemplate> =
  Object.fromEntries([
    defineRole('Admin', 'Admin', SystemRoleType.Admin, [
      'Applications.Full',
      'MoveIns.Full',
      'MoveOuts.Full',
      'Renewals.Full',
      'WorkOrders.Full',
      'Delinquency.Full',
      'DueDiligences.Full',
      'Leases.Full',
      'Units.Full',
      'Sales.Full',
      'Ledgers.Full',
      'Budgets.Full',
      'Residents.Full',
      'Policies.Full',
      'PropertyKPIs.Full',
      'LeaseAudits.Full',
      'LeaseAuditOverrides.Full',
      'LeaseAuditOverrideRequests.Full',
      'LeaseAuditAIIssueRequests.Full',
      'LeaseAuditDisallowAudits.Full',
      'Users.Create',
      'Users.Read',
      'Users.Update',
      'Users.Delete',
      'UserRoles.Read',
      'UserRoles.Full',
      'Configurations.Full',
    ]),
    defineRole(
      'PropertyManager',
      'Property Manager',
      SystemRoleType.PropertyManager,
      [
        'Applications.Full',
        'MoveIns.Full',
        'MoveOuts.Full',
        'Renewals.Full',
        'Leases.Full',
        'Units.Full',
        'WorkOrders.Full',
        'Delinquency.Full',
        'Sales.Full',
        'Ledgers.Full',
        'Budgets.Full',
        'Residents.Full',
        'Policies.Read',
        'PropertyKPIs.Full',
        'LeaseAudits.Full',
        'LeaseAuditOverrides.Read',
        'LeaseAuditOverrideRequests.Full',
        'LeaseAuditAIIssueRequests.Full',
        'WorkOrdersCorporate.Read',
        'Users.Create',
        'Users.Read',
        'Users.Update',
        'UserRoles.Read',
        'UserRoles.Full',
        'Configurations.Full',
      ]
    ),
    defineRole(
      'AssistantPropertyManager',
      'Assistant Property Manager',
      SystemRoleType.AssistantPropertyManager,
      [
        'Applications.Full',
        'MoveIns.Full',
        'MoveOuts.Full',
        'Renewals.Full',
        'Leases.Full',
        'Units.Full',
        'LeaseAudits.Full',
        'WorkOrders.Full',
        'Delinquency.Full',
        'Sales.Full',
        'Ledgers.Full',
        'Budgets.Full',
        'Residents.Full',
        'Policies.Read',
        'PropertyKPIs.Full',
        'LeaseAuditOverrideRequests.Read',
        'LeaseAuditAIIssueRequests.Full',
        'WorkOrdersCorporate.Read',
      ]
    ),
    defineRole('LeasingAgent', 'Leasing Agent', SystemRoleType.LeasingAgent, [
      'Applications.Full',
      'MoveIns.Full',
      'MoveOuts.Full',
      'Renewals.Full',
      'Leases.Full',
      'Units.Full',
      'LeaseAudits.Read',
      'WorkOrders.Full',
      'Delinquency.Full',
      'Sales.Full',
      'Ledgers.Full',
      'Residents.Full',
      'Policies.Full',
      'PropertyKPIs.Read',
      'LeaseAuditOverrideRequests.Read',
      'LeaseAuditAIIssueRequests.Full',
      'WorkOrdersCorporate.Read',
    ]),
    defineRole(
      'MaintenanceSupervisor',
      'Maintenance Supervisor',
      SystemRoleType.MaintenanceSupervisor,
      [
        'Applications.Read',
        'MoveIns.Read',
        'MoveOuts.Read',
        'Renewals.Read',
        'Leases.Read',
        'Units.Read',
        'WorkOrders.Full',
        'Policies.Read',
        'WorkOrdersCorporate.Read',
      ]
    ),
    defineRole(
      'MaintenanceTech',
      'Maintenance Tech',
      SystemRoleType.MaintenanceTech,
      [
        'Applications.Read',
        'MoveIns.Read',
        'MoveOuts.Read',
        'Renewals.Read',
        'Leases.Read',
        'Units.Read',
        'WorkOrders.Full',
        'Policies.Read',
        'WorkOrdersCorporate.Read',
      ]
    ),
    defineRole(
      'RegionalManager',
      'Regional Manager',
      SystemRoleType.RegionalManager,
      [
        'Applications.Full',
        'MoveIns.Full',
        'MoveOuts.Full',
        'Renewals.Full',
        'Leases.Full',
        'Units.Full',
        'LeaseAudits.Full',
        'WorkOrders.Full',
        'Delinquency.Full',
        'Sales.Full',
        'Ledgers.Full',
        'Budgets.Full',
        'Residents.Full',
        'Policies.Read',
        'PropertyKPIs.Read',
        'LeaseAuditOverrides.Full',
        'LeaseAuditOverrideRequests.Full',
        'LeaseAuditAIIssueRequests.Full',
        'WorkOrdersCorporate.Read',
        'Configurations.Full',
        'Users.Create',
        'Users.Read',
        'Users.Update',
        'Users.Delete',
        'UserRoles.Read',
        'UserRoles.Full',
      ]
    ),
    defineRole(
      'MaintenanceDirector',
      'Maintenance Director',
      SystemRoleType.MaintenanceDirector,
      [
        'WorkOrders.Full',
        'Units.Read',
        'Policies.Read',
        'PropertyKPIs.Read',
        'WorkOrdersCorporate.Full',
      ]
    ),
    defineRole(
      'ComplianceManager',
      'Compliance Manager',
      SystemRoleType.ComplianceManager,
      [
        'Policies.Full',
        'PropertyKPIs.Read',
        'Leases.Full',
        'Units.Read',
        'LeaseAudits.Full',
        'LeaseAuditOverrides.Full',
        'LeaseAuditOverrideRequests.Full',
        'LeaseAuditAIIssueRequests.Full',
        'LeaseAuditDisallowAudits.Full',
        'Configurations.Full',
      ]
    ),
    defineRole(
      'RegionalDirector',
      'Regional Director',
      SystemRoleType.RegionalDirector,
      [
        'Applications.Full',
        'MoveIns.Full',
        'MoveOuts.Full',
        'Renewals.Full',
        'Leases.Full',
        'Units.Full',
        'LeaseAudits.Full',
        'WorkOrders.Full',
        'Delinquency.Full',
        'Sales.Full',
        'Ledgers.Full',
        'Budgets.Full',
        'Residents.Full',
        'Policies.Read',
        'PropertyKPIs.Read',
        'LeaseAuditOverrides.Full',
        'LeaseAuditOverrideRequests.Full',
        'LeaseAuditAIIssueRequests.Full',
        'WorkOrdersCorporate.Read',
        'Configurations.Full',
        'Users.Create',
        'Users.Read',
        'Users.Update',
        'Users.Delete',
        'UserRoles.Read',
        'UserRoles.Full',
      ]
    ),
    defineRole(
      'VicePresident',
      'Vice President',
      SystemRoleType.VicePresident,
      [
        'Applications.Full',
        'MoveIns.Full',
        'MoveOuts.Full',
        'Renewals.Full',
        'Leases.Full',
        'Units.Full',
        'LeaseAudits.Full',
        'WorkOrders.Full',
        'Delinquency.Full',
        'DueDiligences.Full',
        'Sales.Full',
        'Ledgers.Full',
        'Budgets.Full',
        'Residents.Full',
        'Policies.Read',
        'PropertyKPIs.Read',
        'LeaseAuditOverrides.Full',
        'LeaseAuditOverrideRequests.Full',
        'LeaseAuditAIIssueRequests.Full',
        'WorkOrdersCorporate.Read',
        'Configurations.Full',
        'Users.Create',
        'Users.Read',
        'Users.Update',
        'Users.Delete',
        'UserRoles.Read',
        'UserRoles.Full',
      ]
    ),
    defineRole(
      'TransitionDepartmentUser',
      'Transition Department User',
      SystemRoleType.TransitionsUser,
      [
        'Leases.Full',
        'Units.Full',
        'LeaseAudits.Full',
        'DueDiligences.Full',
        'Ledgers.Full',
        'Residents.Full',
        'PropertyKPIs.Read',
        'LeaseAuditOverrides.Full',
        'LeaseAuditOverrideRequests.Full',
        'LeaseAuditAIIssueRequests.Full',
        'Configurations.Full',
        'Transitions_Transition.Full',
        'Transitions_Template.Full',
        'Transitions_Document.Full',
        'Transitions_Note.Full',
        'Transitions_DocumentTask.Full',
        'Transitions_DocumentRequest.Full',
        'Transitions_Collection.Full',
        'Transitions_Company.Full',
        'Transitions_User.Full',
      ]
    ),
    defineRole(
      'TransitionsGuestUser',
      'Transitions Guest User',
      SystemRoleType.TransitionsGuestUser,
      [
        'Transitions_Transition.Read',
        'Transitions_Document.Full',
        'Transitions_Note.Full',
        'Transitions_DocumentTask.Read',
        'Transitions_Collection.Read',
        'Units.Read',
      ]
    ),
    defineRole(
      'TransitionsAdmin',
      'Transitions Admin',
      SystemRoleType.TransitionsAdmin,
      [
        'Transitions_Transition.Full',
        'Transitions_Template.Full',
        'Transitions_Document.Full',
        'Transitions_Note.Full',
        'Transitions_DocumentTask.Full',
        'Transitions_DocumentRequest.Full',
        'Transitions_Collection.Full',
        'Transitions_Company.Full',
        'Transitions_User.Full',
        'Units.Full',
      ]
    ),
    defineRole(
      'LeaseAuditUserReadOnly',
      'Lease Audit User (Read Only)',
      SystemRoleType.Other,
      [
        'LeaseAudits.Read',
        'LeaseAuditOverrides.Read',
        'Residents.Read',
        'Leases.Read',
        'Units.Read',
      ]
    ),
    defineRole('Other', 'Other', SystemRoleType.Other, [
      'Applications.Read',
      'MoveIns.Read',
      'MoveOuts.Read',
      'Renewals.Read',
      'Leases.Read',
      'Units.Read',
      'WorkOrders.Full',
      'Policies.Read',
      'WorkOrdersCorporate.Read',
    ]),
  ]);

export const getTemplateRoleComboboxOptions = (): ComboboxOption[] =>
  Object.entries(ROLE_PERMISSION_TEMPLATES)
    .map(([name, template]) => ({
      value: `template:${name}`,
      label: template.label,
      group: 'Role templates',
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const getRoleTemplateLabel = (templateName: string): string | null =>
  ROLE_PERMISSION_TEMPLATES[templateName]?.label ?? null;
